import { Controller } from "@hotwired/stimulus"
import { getI18nMessage } from "../lib/i18n_helper"

// Real-time form validation with animations
// Usage: data-controller="form-validation" data-action="input->form-validation#validate"
export default class extends Controller {
  static targets = ["field", "error"]

  connect() {
    // Pre-load i18n messages for password strength (called once on connect)
    this.passwordStrengthMessages = {
      weak: getI18nMessage(this.element, 'passwordStrengthWeak', 'Weak'),
      fair: getI18nMessage(this.element, 'passwordStrengthFair', 'Fair'),
      good: getI18nMessage(this.element, 'passwordStrengthGood', 'Good'),
      strong: getI18nMessage(this.element, 'passwordStrengthStrong', 'Strong'),
      prefix: getI18nMessage(this.element, 'passwordStrengthPrefix', 'Password strength: '),
      tooShort: getI18nMessage(this.element, 'passwordStrengthTooShort', 'Too short')
    }
  }

  validate(event) {
    const input = event.target
    const errorTarget = this.errorTargets.find(el =>
      el.dataset.fieldName === input.name
    )

    if (!input.validity.valid) {
      this.showError(input, errorTarget)
    } else {
      this.hideError(input, errorTarget)
    }
  }

  showError(input, errorTarget) {
    // Add error styling to input - remove ALL green classes first
    input.classList.remove(
      "border-charcoal-300",
      "border-green-500",
      "focus:border-green-600",
      "focus:ring-green-600"
    )
    input.classList.add("border-red-500", "focus:border-red-600", "focus:ring-red-600")

    // Show error message with animation
    if (errorTarget) {
      errorTarget.textContent = input.validationMessage
      errorTarget.classList.remove("hidden")
      errorTarget.classList.add("animate-slide-down")

      // Remove animation class after it completes
      setTimeout(() => {
        errorTarget.classList.remove("animate-slide-down")
      }, 300)
    }

    // Dispatch event for scroll-focus controller to scroll to error
    this.dispatch("error:shown", {
      detail: { element: input, fieldName: input.name }
    })
  }

  hideError(input, errorTarget) {
    // Add success styling to input - remove ALL red classes first
    input.classList.remove(
      "border-charcoal-300",
      "border-red-500",
      "focus:border-red-600",
      "focus:ring-red-600"
    )
    input.classList.add("border-green-500", "focus:border-green-600", "focus:ring-green-600")

    // Hide error message
    if (errorTarget) {
      errorTarget.classList.add("hidden")
    }
  }

  // Password strength indicator
  // Usage: data-password-strength-levels="['Weak', 'Fair', 'Good', 'Strong']"
  //        data-password-strength-prefix="Password strength: "
  //        data-password-strength-too-short="Too short"
  checkPasswordStrength(event) {
    const password = event.target.value
    const strengthIndicator = this.element.querySelector('[data-password-strength]')

    if (!strengthIndicator) return

    // Get localized strings from data attributes (fallback to i18n messages)
    const strengthLevels = strengthIndicator.dataset.passwordStrengthLevels
      ? JSON.parse(strengthIndicator.dataset.passwordStrengthLevels)
      : [
          this.passwordStrengthMessages.weak,
          this.passwordStrengthMessages.fair,
          this.passwordStrengthMessages.good,
          this.passwordStrengthMessages.strong
        ]
    const prefix = strengthIndicator.dataset.passwordStrengthPrefix || this.passwordStrengthMessages.prefix
    const tooShort = strengthIndicator.dataset.passwordStrengthTooShort || this.passwordStrengthMessages.tooShort

    let strength = 0
    if (password.length >= 8) strength++
    if (password.match(/[a-z]/) && password.match(/[A-Z]/)) strength++
    if (password.match(/[0-9]/)) strength++
    if (password.match(/[^a-zA-Z0-9]/)) strength++

    const strengthColors = ['text-red-600', 'text-yellow-600', 'text-green-600', 'text-green-700']

    strengthIndicator.textContent = `${prefix}${strengthLevels[strength - 1] || tooShort}`
    strengthIndicator.className = `text-xs ${strengthColors[strength - 1] || 'text-red-600'} animate-fade-in`
  }
}
